
import echarts, { EChartOption } from 'echarts'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import ResizeMixin from '../mixins/resize'
import {
  dateToDatezone,
  dateToDatezoneDHM,
  dateToDatezoneHM
} from '@/utils/dateTime'
import { AppModule } from '@/store/modules/app'

@Component({
  name: 'LineChart'
})
export default class extends mixins(ResizeMixin) {
  @Prop({ default: 'chart' }) private className!: string
  @Prop({ default: 'chart' }) private id!: string
  @Prop({ default: '100%' }) private width!: string
  @Prop({ default: '400px' }) private height!: string
  @Prop({ required: true }) private data!: any[]
  @Prop({ required: true }) private unitType!: string
  @Prop({ required: true }) private isLoop!: boolean

  get language () {
    return AppModule.language
  }

  private xLine: string[] = []
  private totalData: any[] = []
  private succeededData: any[] = []
  private rateData: any[] = []
  private legend: string[] = []
  private fields: string[] = []
  private options = {
    // 头部标题
    title: {
      top: 20,
      text: '',
      textStyle: {
        fontWeight: 'normal',
        fontSize: 18,
        color: '#000000'
      },
      left: '1%'
    },
    tooltip: {
      trigger: 'axis',
      show: true,
      transitionDuration: 0 //echart防止tooltip的抖动
    },
    // 图例组件展现了不同系列的标记(symbol)，颜色和名字。可以通过点击图例控制哪些系列不显示
    legend: {
      top: 20,
      icon: 'rect',
      itemWidth: 14,
      itemHeight: 5,
      itemGap: 13,
      data: this.legend,
      right: '4%',
      textStyle: {
        fontSize: 14,
        color: '#000000'
      }
    },
    // 直角坐标系内绘图网格，单个 grid 内最多可以放置上下两个 X 轴，左右两个 Y 轴
    grid: {
      top: 100,
      left: '2%',
      right: '2%',
      bottom: '50',
      containLabel: true
    },
    dataZoom: [
      {
        type: 'slider'
      }
    ],
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: '#000000'
          }
        },
        data: this.xLine
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: this.id === 'rate' ? '%' : '',
        offset: 10,
        axisTick: {
          show: false
        },
        axisLine: {
          lineStyle: {
            color: '#57617B'
          }
        },
        axisLabel: {
          margin: 10,
          fontSize: 14
        },
        splitLine: {
          lineStyle: {
            color: '#eeeeee'
          }
        }
      }
    ],
    series:
      this.id === 'amount' || this.id === 'volume'
        ? [
            {
              name: this.legend[0],
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 5,
              showSymbol: false,
              lineStyle: {
                width: 1
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(
                  0,
                  0,
                  0,
                  1,
                  [
                    {
                      offset: 0,
                      color: 'rgba(137, 189, 27, 0.3)'
                    },
                    {
                      offset: 0.8,
                      color: 'rgba(137, 189, 27, 0)'
                    }
                  ],
                  false
                ),
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowBlur: 10
              },
              itemStyle: {
                color: '#67C23A',
                borderColor: 'rgba(137,189,2,0.27)',
                borderWidth: 12
              },
              data: this.succeededData
            },
            {
              name: this.legend[1],
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 5,
              showSymbol: false,
              lineStyle: {
                width: 1
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(
                  0,
                  0,
                  0,
                  1,
                  [
                    {
                      offset: 0,
                      color: 'rgba(0, 136, 212, 0.3)'
                    },
                    {
                      offset: 0.8,
                      color: 'rgba(0, 136, 212, 0)'
                    }
                  ],
                  false
                ) as any,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowBlur: 10
              },
              itemStyle: {
                color: '#409EFF',
                borderColor: 'rgba(0,136,212,0.2)',
                borderWidth: 12
              },
              data: this.totalData
            }
          ]
        : [
            {
              name: this.legend[0],
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 5,
              showSymbol: false,
              lineStyle: {
                width: 1
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(
                  0,
                  0,
                  0,
                  1,
                  [
                    {
                      offset: 0,
                      color: 'rgba(0, 136, 212, 0.3)'
                    },
                    {
                      offset: 0.8,
                      color: 'rgba(0, 136, 212, 0)'
                    }
                  ],
                  false
                ) as any,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowBlur: 10
              },
              itemStyle: {
                color: 'rgb(0,136,212)',
                borderColor: 'rgba(0,136,212,0.2)',
                borderWidth: 12
              },
              data: this.rateData
            }
          ]
  }
  @Watch('id', { immediate: true })
  private onIdChange () {
    switch (this.id) {
      case 'amount':
        if (this.language === 'en') {
          this.options.title.text = 'Transaction Amountuests'
          this.legend = ['Succeeded Amount', 'Total Amount']
          this.fields = ['succeed_amount', 'total_amount']
        }
        if (this.language === 'zh_cn') {
          this.options.title.text = '交易金额统计数据'
          this.legend = ['成功的金额 ', '总金额']
          this.fields = ['succeed_amount', 'total_amount']
        }
        if (this.language === 'zh_tw') {
          this.options.title.text = '交易金額統計數據'
          this.legend = ['成功的金額 ', '总金額']
          this.fields = ['succeed_amount', 'total_amount']
        }
        break
      case 'volume':
        if (this.language === 'en') {
          this.options.title.text = 'Transaction Volume'
          this.legend = ['Succeeded Volume', 'Total Volume']
          this.fields = ['succeed_volume', 'total_volume']
        }
        if (this.language === 'zh_cn') {
          this.options.title.text = '交易数量'
          this.legend = ['成功数量', '总数量']
          this.fields = ['succeed_volume', 'total_volume']
        }
        if (this.language === 'zh_tw') {
          this.options.title.text = '交易數量'
          this.legend = ['成功數量', '总數量']
          this.fields = ['succeed_volume', 'total_volume']
        }
        break
      case 'rate':
        if (this.language === 'en') {
          this.options.title.text = 'Success Rate'
          this.fields = ['rate']
        }
        if (this.language === 'zh_cn') {
          this.options.title.text = '成功率'
          this.fields = ['rate']
        }
        if (this.language === 'zh_tw') {
          this.options.title.text = '成功率'
          this.fields = ['rate']
        }
        break
      case 'balance':
        if (this.language === 'en') {
          this.options.title.text = 'Balance'
          this.fields = ['balance']
        }
        if (this.language === 'zh_cn') {
          this.options.title.text = '余额'
          this.fields = ['balance']
        }
        if (this.language === 'zh_tw') {
          this.options.title.text = '餘額'
          this.fields = ['balance']
        }
        break
      default:
        break
    }
  }

  @Watch('data', { immediate: true })
  private onDataChange () {
    let xLine: any = []
    let succeededData: any = []
    let totalData: any = []
    let rateData: any = []

    // x轴时间转换
    let timeFn: any = null
    if (this.unitType.includes('m')) {
      timeFn = dateToDatezoneHM
    } else if (this.unitType.includes('h')) {
      timeFn = dateToDatezoneDHM
    } else if (this.unitType.includes('d')) {
      timeFn = dateToDatezone
    }
    this.data.forEach((item: any) => {
      xLine.push(timeFn(item.time * 1000))
      if (this.fields.length === 2) {
        succeededData.push(item[this.fields[0]])
        totalData.push(item[this.fields[1]])
      } else {
        rateData.push(item[this.fields[0]])
      }
    })
    if (this.isLoop) {
      if (this.id === 'amount' || this.id === 'volume') {
        const len1 = succeededData.length
        this.options.series[0].data[len1 - 1] = succeededData[len1 - 1]
        const len2 = totalData.length
        this.options.series[1].data[len2 - 1] = totalData[len2 - 1]
      } else {
        const len = rateData.length
        this.options.series[0].data[len - 1] = rateData[len - 1]
      }
    } else {
      this.xLine = xLine
      this.options.xAxis[0].data = xLine
      this.options.legend.data = this.legend
      if (this.id === 'amount' || this.id === 'volume') {
        this.options.series[0].data = succeededData
        this.options.series[1].data = totalData
        this.options.series[0].name = this.legend[0]
        this.options.series[1].name = this.legend[1]
      } else {
        this.options.series[0].data = rateData
      }
    }
    this.chart?.setOption(this.options as EChartOption<EChartOption.SeriesPie>)
  }

  beforeDestroy () {
    if (!this.chart) {
      return
    }
    this.chart.dispose()
    this.chart = null
  }

  mounted () {
    this.$nextTick(() => {
      this.initChart()
    })
  }

  private initChart () {
    this.chart = echarts.init(
      document.getElementById(this.id) as HTMLDivElement
    )
    this.chart.setOption(this.options as EChartOption<EChartOption.SeriesPie>)
  }
}
